/**
 * Created by Ning Jiang on 2/7/2018.
 */

import {SpineTween} from "@nolimitcity/slot-game/bin/core/animation/SpineTween";
import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {IWinRatioConfig, WinRatioType} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {Timeline, TimelineLite, TweenLite} from "gsap";
import {ThorGameConfig} from "../../ThorGameConfig";
import {CoinsFountain} from "./CoinsFountain";

export class ThorBigWinView extends BaseView {

    private static BIG_WIN_INIT_LANDSCAPE:string = "InitialBigWinLandscape";
    private static BIG_WIN_INIT_PORTRAIT:string = "InitialBigWinPortrait";
    private static BIG_WIN_01_WIN:string = "WinTung";
    private static BIG_WIN_01_IDLE:string = "IdleTung";
    private static BIG_WIN_02_WIN:string = "WinMikid";
    private static BIG_WIN_02_IDLE:string = "IdleMikid";
    private static BIG_WIN_03_WIN:string = "WinGed";
    private static BIG_WIN_03_IDLE:string = "IdleGed";
    private static SPINE_ASSET_NAME:string = "bigWinSpine";
    private _coinsFountain:CoinsFountain;

    private _layer:ScalableLayer;
    private _isShowing:boolean = false;
    private _dimmer:PIXI.Graphics;
    private _bigWinAnimations:PIXI.spine.Spine;
    private _placeHolderText:PIXI.Text;

    constructor() {
        super();
        this._layer = StageManager.getLayer(ThorGameConfig.instance.LAYERS.BIG_WIN.name);
        this._coinsFountain = new CoinsFountain(this._layer);
    }

    protected initAnimations():void {
        this._placeHolderText = new PIXI.Text("", {"fontSize" : 180, "fill" : "#ffffff"});
        this._placeHolderText.anchor.set(0.5, 0.5);
        this._placeHolderText.alpha = 0;
        this._layer.addChild(this._placeHolderText);

        this._dimmer = new PIXI.Graphics();
        this._dimmer.alpha = 0;
        this._layer.addChild(this._dimmer);

        this._bigWinAnimations = new PIXI.spine.Spine(GameResources.getSpineAsset(ThorBigWinView.SPINE_ASSET_NAME));
        this._bigWinAnimations.alpha = 0;
        const bigWinContainer:PIXI.Sprite = new PIXI.Sprite();
        bigWinContainer.anchor.set(0.5, 0.5);
        bigWinContainer.addChild(this._bigWinAnimations);
        bigWinContainer.position.set(ThorGameConfig.instance.GAME_WIDTH / 2, (ThorGameConfig.instance.GAME_HEIGHT / 2) - 60);
        this._layer.addChild(bigWinContainer);
    }

    protected onResize(resizeData:IResizeData):void {
        if(!this._isShowing) {
            this._isResizeDirty = true;
            return;
        }

        this._isResizeDirty = false;

        const screenWidth:number = Math.ceil(resizeData.width / this._layer.scale.x);
        const screenHeight:number = Math.ceil(resizeData.height / this._layer.scale.y);
        const leftOffset:number = Math.floor(-this._layer.x / this._layer.scale.x);
        const topOffset:number = Math.floor(-this._layer.y / this._layer.scale.y);

        this._dimmer.clear();
        this._dimmer.beginFill(0x000000, 0.8);
        this._dimmer.drawRect(leftOffset, topOffset, screenWidth, screenHeight);
    }

    public getBigWinLevelAnimation(level:number, winRatioConfig:IWinRatioConfig):Timeline {
        this._isShowing = true;
        if(this._isResizeDirty) {
            this.onResize(this._resizeData);
        }

        const timeline:TimelineLite = new TimelineLite();
        timeline.add([
            TweenLite.to(this._dimmer, 0.1, {alpha : 1}),
            TweenLite.to(this._bigWinAnimations, 0.1, {alpha : 1}),
            () => this.updateBigWinLevelAnimation(winRatioConfig.type!),
            TweenLite.to(this._placeHolderText.scale, winRatioConfig.duration, {x : 1}),
            () => this._coinsFountain.startFountain(level)
        ]);

        return timeline;
    }

    private updateBigWinLevelAnimation(text:WinRatioType):void {
        const timeline:TimelineLite = new TimelineLite();
        timeline.add(new SpineTween(this._bigWinAnimations, 0, this.getBigWinWinName(text), 0));
        timeline.add(new SpineTween(this._bigWinAnimations, 0, this.getBigWinIdleName(text), -1));
        timeline.play();
    }

    private getBigWinWinName(winType:WinRatioType):string {
        switch(winType) {
            case WinRatioType.SUPER_MEGA_WIN :
                return ThorBigWinView.BIG_WIN_03_WIN;

            case WinRatioType.MEGA_WIN :
                return ThorBigWinView.BIG_WIN_02_WIN;

            case WinRatioType.BIG_WIN :
                return ThorBigWinView.BIG_WIN_01_WIN;

            default :
                return ThorBigWinView.BIG_WIN_01_WIN;
        }
    }

    private getBigWinIdleName(winType:WinRatioType):string {
        switch(winType) {
            case WinRatioType.SUPER_MEGA_WIN :
                return ThorBigWinView.BIG_WIN_03_IDLE;

            case WinRatioType.MEGA_WIN :
                return ThorBigWinView.BIG_WIN_02_IDLE;

            case WinRatioType.BIG_WIN :
                return ThorBigWinView.BIG_WIN_01_IDLE;

            default :
                return ThorBigWinView.BIG_WIN_01_IDLE
        }
    }

    public playEndingAnimation(endWinRatioConfig:IWinRatioConfig, isAborted:boolean):TimelineLite {
        let delay:number = 0.2;
        const timeline:TimelineLite = new TimelineLite();
        if(isAborted == true) {
            delay = 0;
            timeline.add(new SpineTween(this._bigWinAnimations, 0, this.getBigWinWinName(endWinRatioConfig.type!), 0));
        }
        timeline.add(() => this._coinsFountain.stopFountain(), 0);
        timeline.add([
            new SpineTween(this._bigWinAnimations, 0, this.getBigWinIdleName(endWinRatioConfig.type!), -1),
            TweenLite.to(this._dimmer, 0.4, {alpha : 0, delay : delay}),
            TweenLite.fromTo(this._bigWinAnimations, 0.4, {alpha : 1}, {alpha : 0, delay : delay})
        ], 0);
        timeline.add(() => this._isShowing = false);
        return timeline;
    }
}